import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Grain } from "./Grain";
import { FloatingWhatsApp } from "./FloatingWhatsApp";
import { staticPages } from "../data/staticPages";

// Renders a bilingual static content page from /data/staticPages.js
export const StaticPage = ({ slug }) => {
    const { lang, t } = useLang();
    const page = staticPages[slug];
    if (!page) return null;

    const isFaq = slug === "faq";

    return (
        <div className="min-h-screen bg-[color:var(--kp-cream)]" data-testid={`static-page-${slug}`}>
            <Grain />
            <Header />
            <main className="pt-28 md:pt-36 pb-24">
                <div className="max-w-[900px] mx-auto px-6 md:px-10">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 hover:text-[color:var(--kp-red)] mb-6"
                        data-testid="static-back-home"
                    >
                        <ArrowLeft size={12} /> {lang === "en" ? "Home" : "హోమ్"}
                    </Link>
                    <div className="flex items-center gap-3 mb-4">
                        <span className="w-8 h-px bg-[color:var(--kp-red)]" />
                        <span
                            className={`text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 ${
                                lang === "te" ? "font-telugu-body" : ""
                            }`}
                        >
                            {t(page.eyebrow)}
                        </span>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                        className={`text-6xl md:text-8xl leading-[0.95] tracking-editorial mb-6 whitespace-pre-line ${
                            lang === "en" ? "font-serif-display" : "font-telugu-display"
                        }`}
                        data-testid="static-title"
                    >
                        {t(page.title)
                            .split("\n")
                            .map((line, i) => (
                                <span key={i} className="block">
                                    {i === 1 ? (
                                        <em
                                            className={`text-[color:var(--kp-red)] ${
                                                lang === "en" ? "italic" : "not-italic"
                                            }`}
                                        >
                                            {line}
                                        </em>
                                    ) : (
                                        line
                                    )}
                                </span>
                            ))}
                    </motion.h1>
                    <p
                        className={`text-lg md:text-xl text-[color:var(--kp-ink)]/75 leading-relaxed max-w-2xl mb-16 ${
                            lang === "te" ? "font-telugu-body" : ""
                        }`}
                        data-testid="static-intro"
                    >
                        {t(page.intro)}
                    </p>

                    {isFaq ? (
                        <ul className="divide-y divide-[color:var(--kp-ink)]/15" data-testid="faq-list">
                            {page.items.map((it, i) => (
                                <motion.li
                                    key={i}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.6, delay: i * 0.03 }}
                                    className="py-6 md:py-8"
                                    data-testid={`faq-item-${i}`}
                                >
                                    <div className="grid grid-cols-12 gap-4">
                                        <div className="col-span-2 md:col-span-1 text-[color:var(--kp-red)] font-serif-display italic text-2xl">
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className="col-span-10 md:col-span-11">
                                            <h3
                                                className={`text-xl md:text-2xl mb-3 tracking-editorial ${
                                                    lang === "en"
                                                        ? "font-serif-display"
                                                        : "font-telugu-display"
                                                }`}
                                            >
                                                {t(it.q)}
                                            </h3>
                                            <p
                                                className={`text-base text-[color:var(--kp-ink)]/75 leading-relaxed ${
                                                    lang === "te" ? "font-telugu-body" : ""
                                                }`}
                                            >
                                                {t(it.a)}
                                            </p>
                                        </div>
                                    </div>
                                </motion.li>
                            ))}
                        </ul>
                    ) : (
                        <div className="space-y-14 md:space-y-16">
                            {page.sections.map((sec, i) => (
                                <motion.section
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.7, delay: i * 0.05 }}
                                    className="grid grid-cols-12 gap-4 md:gap-8"
                                    data-testid={`static-section-${i}`}
                                >
                                    <div className="col-span-2 md:col-span-1 text-[color:var(--kp-red)] font-serif-display italic text-2xl">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <div className="col-span-10 md:col-span-11">
                                        <h3
                                            className={`text-2xl md:text-3xl mb-3 tracking-editorial ${
                                                lang === "en" ? "font-serif-display" : "font-telugu-display"
                                            }`}
                                        >
                                            {t(sec.h)}
                                        </h3>
                                        <p
                                            className={`text-base md:text-lg text-[color:var(--kp-ink)]/75 leading-relaxed ${
                                                lang === "te" ? "font-telugu-body" : ""
                                            }`}
                                        >
                                            {t(sec.p)}
                                        </p>
                                    </div>
                                </motion.section>
                            ))}
                        </div>
                    )}
                </div>
            </main>
            <Footer />
            <FloatingWhatsApp />
        </div>
    );
};

export const AboutPage = () => <StaticPage slug="about" />;
export const FAQPage = () => <StaticPage slug="faq" />;
export const StoragePage = () => <StaticPage slug="storage" />;
export const ShippingPage = () => <StaticPage slug="shipping" />;
export const PrivacyPage = () => <StaticPage slug="privacy" />;
export const TermsPage = () => <StaticPage slug="terms" />;
export const ContactHelpPage = () => <StaticPage slug="contact_page" />;
